import { motion } from 'framer-motion';
import { Eye, EyeOff, Fingerprint, Lock, ShieldCheck } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import validator from 'validator';
import api from '../config/axiosConfig';

// ── Grille de fond ────────────────────────────────────────────────────────────
const PerspectiveGrid = () => (
  <div
    style={{
      position: 'absolute',
      inset: 0,
      pointerEvents: 'none',
      backgroundImage: `
        linear-gradient(to right, rgba(34,211,238,0.06) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(245,158,11,0.06) 1px, transparent 1px)
      `,
      backgroundSize: '80px 80px',
      maskImage: 'radial-gradient(circle at 50% 50%, black 40%, transparent 100%)',
      WebkitMaskImage: 'radial-gradient(circle at 50% 50%, black 40%, transparent 100%)',
    }}
  />
);

// ── Barre de scan animée ──────────────────────────────────────────────────────
const ScanLine = () => (
  <motion.div
    animate={{ top: ['-2%', '102%'] }}
    transition={{ duration: 5, repeat: Infinity, ease: 'linear', repeatDelay: 1.5 }}
    style={{
      position: 'absolute',
      left: 0,
      right: 0,
      height: '1px',
      background: 'linear-gradient(90deg, transparent 0%, rgba(34,211,238,0.4) 30%, rgba(245,158,11,0.5) 70%, transparent 100%)',
      zIndex: 10,
      pointerEvents: 'none',
    }}
  />
);

// ── Champ de saisie natif ─────────────────────────────────────────────────────
const HudInput = ({ label, value, onChange, type = 'text', error, hint, icon: Icon, rightSlot, disabled = false }) => {
  const [focused, setFocused] = useState(false);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem' }}>
      <label
        style={{
          fontFamily: 'monospace',
          fontSize: '0.5rem',
          letterSpacing: '0.3em',
          textTransform: 'uppercase',
          color: disabled 
            ? 'rgba(255,255,255,0.4)' 
            : focused 
              ? '#22d3ee' 
              : error 
                ? 'rgba(239,68,68,0.7)' 
                : 'rgba(255,255,255,0.25)',
          transition: 'color 0.2s',
        }}
      >
        {label}
      </label>
      <div
        style={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          background: disabled 
            ? 'rgba(255,255,255,0.01)' 
            : focused 
              ? 'rgba(34,211,238,0.03)' 
              : 'rgba(255,255,255,0.02)',
          border: `1px solid ${
            disabled 
              ? 'rgba(255,255,255,0.15)' 
              : error 
                ? 'rgba(239,68,68,0.45)' 
                : focused 
                  ? 'rgba(34,211,238,0.3)' 
                  : 'rgba(255,255,255,0.06)'
          }`,
          transition: 'border-color 0.2s, background 0.2s',
          padding: '0 0.75rem',
          opacity: disabled ? 0.85 : 1,
        }}
      >
        {/* Coins cyber */}
        <span style={{ position: 'absolute', top: -1, left: -1, width: 6, height: 6, borderTop: `1px solid ${focused ? '#22d3ee' : 'rgba(34,211,238,0.2)'}`, borderLeft: `1px solid ${focused ? '#22d3ee' : 'rgba(34,211,238,0.2)'}` }} />
        <span style={{ position: 'absolute', bottom: -1, right: -1, width: 6, height: 6, borderBottom: `1px solid ${focused ? '#f59e0b' : 'rgba(245,158,11,0.2)'}`, borderRight: `1px solid ${focused ? '#f59e0b' : 'rgba(245,158,11,0.2)'}` }} />

        {Icon && (
          <Icon size={13} style={{ color: focused ? '#22d3ee' : 'rgba(255,255,255,0.2)', flexShrink: 0, marginRight: '0.6rem' }} />
        )}

        <input
          type={type}
          value={value}
          onChange={onChange}
          onFocus={() => !disabled && setFocused(true)}
          onBlur={() => !disabled && setFocused(false)}
          disabled={disabled}
          style={{
            flex: 1, 
            background: 'transparent', 
            border: 'none', 
            outline: 'none',
            fontFamily: 'monospace', 
            fontSize: '0.75rem', 
            letterSpacing: '0.08em',
            color: disabled ? 'rgba(255,255,255,0.5)' : '#e2e8f0',
            padding: '0.85rem 0', 
            caretColor: disabled ? 'transparent' : '#22d3ee',
            cursor: disabled ? 'not-allowed' : 'text',
          }}
        />
        {rightSlot && <div style={{ flexShrink: 0, marginLeft: '0.5rem' }}>{rightSlot}</div>}
      </div>

      {error && (
        <span style={{ fontFamily: 'monospace', fontSize: '0.46rem', letterSpacing: '0.25em', color: 'rgba(239,68,68,0.8)', textTransform: 'uppercase' }}>
          ▲ {error}
        </span>
      )}
      {!error && hint && (
        <span style={{ fontFamily: 'monospace', fontSize: '0.44rem', letterSpacing: '0.2em', color: 'rgba(255,255,255,0.18)', textTransform: 'uppercase' }}>
          {hint}
        </span>
      )}
    </div>
  );
};

// ── Écran de succès ───────────────────────────────────────────────────────────
const SuccessScreen = () => (
  <motion.div
    initial={{ opacity: 0, scale: 0.95 }}
    animate={{ opacity: 1, scale: 1 }}
    transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
    style={{ width: '100%', maxWidth: 400, padding: '0 1.5rem', zIndex: 20 }}
  >
    <div
      style={{
        position: 'relative',
        background: 'rgba(2,6,23,0.7)',
        backdropFilter: 'blur(24px)',
        border: '1px solid rgba(16,185,129,0.15)',
        overflow: 'hidden',
        padding: '3rem 2rem',
        textAlign: 'center',
      }}
    >
      <span style={{ position: 'absolute', top: -1, left: -1, width: 12, height: 12, borderTop: '2px solid #10b981', borderLeft: '2px solid #10b981' }} />
      <span style={{ position: 'absolute', bottom: -1, right: -1, width: 12, height: 12, borderBottom: '2px solid #f59e0b', borderRight: '2px solid #f59e0b' }} />

      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent, rgba(16,185,129,0.6), rgba(245,158,11,0.5), transparent)' }} />

      <motion.div
        animate={{ rotate: 360 }}
        transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
        style={{
          display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
          width: 64, height: 64, borderRadius: '50%',
          border: '1px solid rgba(16,185,129,0.2)',
          marginBottom: '1.5rem',
        }}
      >
        <ShieldCheck size={28} style={{ color: '#10b981' }} strokeWidth={1.5} />
      </motion.div>

      <p style={{ fontFamily: 'monospace', fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.25em', textTransform: 'uppercase', color: '#10b981', marginBottom: '0.75rem' }}>
        Access_Key_Updated
      </p>
      <p style={{ fontFamily: 'monospace', fontSize: '0.55rem', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', lineHeight: 1.8 }}>
        Séquence validée<br />
        Redirection en cours...
      </p>

      {/* Barre de progression */}
      <div style={{ marginTop: '2rem', height: 1, background: 'rgba(255,255,255,0.06)', position: 'relative', overflow: 'hidden' }}>
        <motion.div
          animate={{ x: ['0%', '100%'] }}
          transition={{ duration: 2.5, ease: 'linear' }}
          style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, transparent, #10b981, transparent)' }}
        />
      </div>
    </div>
  </motion.div>
);

// ── Composant principal ───────────────────────────────────────────────────────
const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const query = new URLSearchParams(location.search);
  const tokenFromUrl = query.get('token') || '';
  const emailFromUrl = query.get('email') || '';

  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  const hasShownToast = useRef(false);

  useEffect(() => {
    if (!tokenFromUrl || !emailFromUrl || !validator.isEmail(emailFromUrl)) {
      if (!hasShownToast.current) {
        toast.error('Lien de réinitialisation invalide ou expiré');
        hasShownToast.current = true;
      }
      navigate('/forgot-password', { replace: true });
    }
  }, [tokenFromUrl, emailFromUrl, navigate]);

  const validateInputs = () => {
    let valid = true;
    setPasswordError('');
    setConfirmError('');
    if (password.length < 8) { setPasswordError('Minimum 8 caractères requis'); valid = false; }
    if (password !== passwordConfirm) { setConfirmError('Les mots de passe ne correspondent pas'); valid = false; }
    return valid;
  };

  const handleReset = useCallback(async (e) => {
    e.preventDefault();
    if (!validateInputs()) return;
    setIsSubmitting(true);
    try {
      await api.post('/auth/reset-password', {
        email: emailFromUrl, password, passwordConfirm, token: tokenFromUrl,
      });
      toast.success('Mot de passe mis à jour → connexion possible');
      setSuccess(true);
      setTimeout(() => navigate('/auth/login', { replace: true }), 2500);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Échec de la mise à jour du mot de passe');
    } finally {
      setIsSubmitting(false);
    }
  }, [password, passwordConfirm, emailFromUrl, tokenFromUrl, navigate]);

  return (
    <div
      style={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: '#020617', position: 'relative', overflow: 'hidden', fontFamily: 'monospace',
      }}
    >
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(circle at 30% 20%, rgba(34,211,238,0.04) 0%, transparent 40%), radial-gradient(circle at 70% 80%, rgba(245,158,11,0.04) 0%, transparent 40%)' }} />
      <PerspectiveGrid />
      <div style={{ position: 'absolute', inset: 0, opacity: 0.025, pointerEvents: 'none', backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`, backgroundRepeat: 'repeat' }} />

      {success ? (
        <SuccessScreen />
      ) : (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
          style={{ width: '100%', maxWidth: 420, padding: '0 1.5rem', zIndex: 20 }}
        >
          <div
            style={{
              position: 'relative', background: 'rgba(2,6,23,0.7)',
              backdropFilter: 'blur(24px)', border: '1px solid rgba(255,255,255,0.06)', overflow: 'hidden',
            }}
          >
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg, transparent 0%, rgba(34,211,238,0.5) 30%, rgba(245,158,11,0.5) 70%, transparent 100%)' }} />
            <ScanLine />
            <span style={{ position: 'absolute', top: -1, left: -1, width: 12, height: 12, borderTop: '2px solid #22d3ee', borderLeft: '2px solid #22d3ee' }} />
            <span style={{ position: 'absolute', bottom: -1, right: -1, width: 12, height: 12, borderBottom: '2px solid #f59e0b', borderRight: '2px solid #f59e0b' }} />

            <div style={{ padding: '2.5rem 2rem 2rem' }}>

              {/* En-tête */}
              <div style={{ textAlign: 'center', marginBottom: '2.5rem' }}>
                <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 52, height: 52, border: '1px solid rgba(245,158,11,0.2)', background: 'rgba(245,158,11,0.04)', position: 'relative', marginBottom: '1.1rem' }}>
                  <span style={{ position: 'absolute', top: -1, left: -1, width: 8, height: 8, borderTop: '2px solid #22d3ee', borderLeft: '2px solid #22d3ee' }} />
                  <span style={{ position: 'absolute', bottom: -1, right: -1, width: 8, height: 8, borderBottom: '2px solid #f59e0b', borderRight: '2px solid #f59e0b' }} />
                  <Lock size={22} style={{ color: '#f59e0b' }} strokeWidth={1.5} />
                </div>

                <div style={{ fontSize: '0.95rem', fontWeight: 700, letterSpacing: '0.06em', color: '#fff', lineHeight: 1.1, marginBottom: '0.35rem' }}>
                  PESA<span style={{ background: 'linear-gradient(90deg,#22d3ee,#f59e0b)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>CORE</span>
                  <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.7rem' }}> · RESET</span>
                </div>
                <p style={{ fontFamily: 'monospace', fontSize: '0.48rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)' }}>
                  Nouvelle clé d'accès
                </p>
              </div>

              {/* Formulaire */}
              <form onSubmit={handleReset} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
                <HudInput
                label="EMAIL_À_RÉINITIALISER"
                value={emailFromUrl}
                onChange={() => {}}
                type="email"
                disabled={true}
                hint="Vérifiez que l'adresse correspond à votre compte"
              />
                <HudInput
                  label="Nouvelle_Access_Key · Mot de passe"
                  value={password}
                  onChange={(e) => { setPassword(e.target.value); setPasswordError(''); }}
                  type={showPassword ? 'text' : 'password'}
                  error={passwordError}
                  hint="8+ car. · majuscule · chiffre · caractère spécial"
                  icon={Lock}
                  rightSlot={
                    <button type="button" onClick={() => setShowPassword(v => !v)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#22d3ee'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}
                      aria-label={showPassword ? 'Masquer' : 'Afficher'}
                    >
                      {showPassword ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  }
                />

                <HudInput
                  label="Confirmer_Access_Key"
                  value={passwordConfirm}
                  onChange={(e) => { setPasswordConfirm(e.target.value); setConfirmError(''); }}
                  type={showConfirm ? 'text' : 'password'}
                  error={confirmError}
                  icon={Fingerprint}
                  rightSlot={
                    <button type="button" onClick={() => setShowConfirm(v => !v)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.2rem', color: 'rgba(255,255,255,0.2)', display: 'flex', alignItems: 'center', transition: 'color 0.2s' }}
                      onMouseEnter={e => e.currentTarget.style.color = '#22d3ee'}
                      onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}
                      aria-label={showConfirm ? 'Masquer' : 'Afficher'}
                    >
                      {showConfirm ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  }
                />

                <motion.button
                  type="submit"
                  disabled={isSubmitting}
                  whileHover={!isSubmitting ? { boxShadow: '0 0 30px rgba(245,158,11,0.35)' } : {}}
                  whileTap={!isSubmitting ? { scale: 0.98 } : {}}
                  style={{
                    marginTop: '0.5rem', width: '100%', padding: '1rem',
                    background: isSubmitting ? 'rgba(255,255,255,0.04)' : 'linear-gradient(135deg, #f59e0b 0%, #fbbf24 50%, #f59e0b 100%)',
                    border: isSubmitting ? '1px solid rgba(255,255,255,0.06)' : 'none',
                    color: isSubmitting ? '#475569' : '#020617',
                    fontFamily: 'monospace', fontSize: '0.6rem', fontWeight: 700,
                    letterSpacing: '0.22em', textTransform: 'uppercase',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    transition: 'background 0.3s, color 0.3s',
                  }}
                >
                  {isSubmitting ? 'Updating_Key_Stream...' : 'Valider_Nouvelle_Clé'}
                </motion.button>
              </form>

              {/* Lien retour */}
              <div style={{ marginTop: '1.75rem', paddingTop: '1.25rem', borderTop: '1px solid rgba(255,255,255,0.04)', textAlign: 'center' }}>
                <button
                  type="button"
                  onClick={() => navigate('/auth/login')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.48rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', transition: 'color 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.5)'}
                  onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.2)'}
                >
                  ← Retour Séquence Authentification
                </button>
              </div>
            </div>
          </div>

          {/* Métadonnées */}
          <div style={{ marginTop: '1.5rem', display: 'flex', justifyContent: 'space-between', opacity: 0.35 }}>
            <span style={{ fontSize: '0.42rem', letterSpacing: '0.22em', color: '#475569', textTransform: 'uppercase' }}>Node_Loc: Password_Reset</span>
            <span style={{ fontSize: '0.42rem', letterSpacing: '0.22em', color: '#475569', textTransform: 'uppercase' }}>Encryption: AES_256</span>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default ResetPassword;